import React, { useMemo, useRef, useState } from "react";
import "./Form.css";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MessageIcon from "@mui/icons-material/Message";
import CheckIcon from "@mui/icons-material/Check";
import ReCAPTCHA from "react-google-recaptcha";

const Form = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    vehicle: "",
    email: "",
    message: "",
    consent: false, // Added consent checkbox state
  });

  const [captchaToken, setCaptchaToken] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" }); // success | error

  const recaptchaRef = useRef(null);

  const RECAPTCHA_SITE_KEY = useMemo(() => {
    const key = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    return typeof key === "string" ? key.trim() : "";
  }, []);

  const FORMSPREE_ENDPOINT = useMemo(() => {
    const url = process.env.REACT_APP_FORMSPREE_ENDPOINT;
    return typeof url === "string" ? url.trim() : "";
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleConsentClick = () => {
    setFormData((prev) => ({
      ...prev,
      consent: !prev.consent,
    }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      phone: "",
      vehicle: "",
      email: "",
      message: "",
      consent: false,
    });
    setCaptchaToken("");
    if (recaptchaRef.current) recaptchaRef.current.reset();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", message: "" });

    // Guards (prevents the "Missing required parameters: sitekey" crash too)
    if (!FORMSPREE_ENDPOINT) {
      setStatus({
        type: "error",
        message:
          "Formspree endpoint missing. Add REACT_APP_FORMSPREE_ENDPOINT in .env and restart.",
      });
      return;
    }

    if (!RECAPTCHA_SITE_KEY) {
      setStatus({
        type: "error",
        message:
          "reCAPTCHA site key missing. Add REACT_APP_RECAPTCHA_SITE_KEY in .env and restart.",
      });
      return;
    }

    if (!captchaToken) {
      setStatus({ type: "error", message: "Please verify you are not a robot." });
      return;
    }

    if (!formData.consent) {
      setStatus({ type: "error", message: "Please consent to being contacted." });
      return;
    }

    setIsSubmitting(true);

    try {
      // Formspree: send JSON payload
      const payload = {
        name: formData.name,
        phone: formData.phone,
        vehicle: formData.vehicle,
        email: formData.email,
        message: formData.message,
        consent: formData.consent,
        // extra field
        "g-recaptcha-response": captchaToken,
      };

      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg =
          data?.errors?.[0]?.message ||
          "Submission failed. Please try again.";
        setStatus({ type: "error", message: msg });
        return;
      }

      setStatus({
        type: "success",
        message: "Thank you for contacting EZ Raider! We will get back to you soon.",
      });
      resetForm();
    } catch (err) {
      setStatus({
        type: "error",
        message: "Network error. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const vehicleOptions = [
    { value: "", label: "Select vehicle type..." },
    { value: "lw", label: "EZ RAIDER LW" },
    { value: "hd2", label: "EZ RAIDER HD2" },
    { value: "hd4", label: "EZ RAIDER HD4" },
    { value: "unsure", label: "Not sure yet" },
  ];

  return (
    <section className="form-section" id="form-section">
      {/* Background Decorative Elements */}
      <div className="bg-decorative-left"></div>
      <div className="bg-decorative-right"></div>

      <div className="form-container">
        <div className="form-header">
          <div className="header-label">
            <span className="label-text">GET IN TOUCH</span>
            <div className="label-underline"></div>
          </div>
          <h1 className="form-heading">LEARN MORE ABOUT EZ RAIDER</h1>
        </div>

        <div className="form-card">
          <div className="card-left">
            <div className="left-content">
              <div className="contact-info-section">
                <h3 className="contact-info-title">CONTACT INFORMATION</h3>

                <div className="contact-info-items">
                  <div className="contact-info-item">
                    <div className="contact-info-icon">
                      <PhoneIcon className="info-icon" />
                    </div>
                    <div className="contact-info-content">
                      <div className="contact-info-label">PHONE</div>
                      <div className="contact-info-value">+1 (555) 123 4567</div>
                    </div>
                  </div>

                  <div className="contact-info-item">
                    <div className="contact-info-icon">
                      <EmailIcon className="info-icon" />
                    </div>
                    <div className="contact-info-content">
                      <div className="contact-info-label">EMAIL</div>
                      <div className="contact-info-value">contact@ezraiderusa.com</div>
                    </div>
                  </div>

                  <div className="contact-info-item">
                    <div className="contact-info-icon">
                      <LocationOnIcon className="info-icon" />
                    </div>
                    <div className="contact-info-content">
                      <div className="contact-info-label">ADDRESS</div>
                      <div className="contact-info-value">EZ Raider US Distribution</div>
                    </div>
                  </div>

                  <div className="contact-info-item message-item">
                    <div className="contact-info-icon">
                      <MessageIcon className="info-icon" />
                    </div>
                    <div className="contact-info-content">
                      <div className="contact-info-label">MESSAGE</div>
                      <div className="contact-info-value">
                        Tell us how you plan to use your EZ Raider and any questions you may have.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="company-info">
                  <div className="company-name">EZ RAIDER US</div>
                  <div className="legal-links">
                    <a href="/privacy-policy" className="legal-link">PRIVACY POLICY</a>
                    <span className="link-separator">|</span>
                    <a href="/terms-conditions" className="legal-link">TERMS & CONDITIONS</a>
                  </div>
                  <div className="copyright">© 2026 EZ RAIDER. ALL RIGHTS RESERVED.</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card-right">
            <div className="form-wrapper">
              <h3 className="form-title">CONTACT US</h3>

              <form className="contact-form" onSubmit={handleSubmit}>
                {/* First Row */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">
                      <span className="label-text">NAME</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Your name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">
                      <span className="label-text">PHONE</span>
                      <span className="required"> *</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="+1 (555) 123-4567"
                      required
                    />
                  </div>
                </div>

                {/* Second Row */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="vehicle" className="form-label">
                      <span className="label-text">VEHICLE INTEREST</span>
                    </label>
                    <div className="select-wrapper">
                      <select
                        id="vehicle"
                        name="vehicle"
                        value={formData.vehicle}
                        onChange={handleChange}
                        className="form-select"
                      >
                        {vehicleOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <div className="select-arrow"></div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="email" className="form-label">
                      <span className="label-text">EMAIL</span>
                      <span className="required"> *</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="your.email@example.com"
                      required
                    />
                    <small className="form-helper">We'll never share your email with anyone else.</small>
                  </div>
                </div>

                {/* Message */}
                <div className="form-group">
                  <label htmlFor="message" className="form-label">
                    <span className="label-text">YOUR MESSAGE</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="form-textarea"
                    placeholder="Tell us how you plan to use your EZ Raider and any questions you may have..."
                    rows="5"
                  />
                </div>

                {/* Consent Checkbox */}
                <div className="form-group">
                  <div 
                    className={`verification-checkbox ${formData.consent ? "checked" : ""}`}
                    onClick={handleConsentClick}
                    role="checkbox"
                    aria-checked={formData.consent}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleConsentClick();
                      }
                    }}
                  >
                    <div className="checkbox-icon">
                      {formData.consent && <CheckIcon className="check-icon" />}
                    </div>
                    <span className="verification-text">
                      I consent to being contacted via SMS or Email by EZ Raider.
                    </span>
                  </div>
                </div>

                {/* ✅ reCAPTCHA (replaces fake checkbox) */}
                <div className="verification-area">
                  {RECAPTCHA_SITE_KEY ? (
                    <div className="recaptcha-wrap">
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={RECAPTCHA_SITE_KEY}
                        onChange={(token) => setCaptchaToken(token || "")}
                        onExpired={() => setCaptchaToken("")}
                      />
                    </div>
                  ) : (
                    <div className="recaptcha-error">
                      Missing reCAPTCHA site key. Check your .env and restart.
                    </div>
                  )}
                </div>

                {/* Status message */}
                {status.message ? (
                  <div
                    className={`form-status ${
                      status.type === "success" ? "form-status--success" : "form-status--error"
                    }`}
                    role="status"
                    aria-live="polite"
                  >
                    {status.message}
                  </div>
                ) : null}

                {/* Submit Button */}
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={!captchaToken || !formData.consent || isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="submitting-text">
                      <span className="spinner"></span>
                      SENDING...
                    </span>
                  ) : (
                    <span className="submit-text">
                      SEND MESSAGE
                      <ArrowForwardIcon className="submit-icon" />
                    </span>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Form;